import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { COUNTRY_CODE, Order } from '@cemex-core/cmx-api';
import { getCountry } from '@cemex/cmx-client-layout';
import { select, Store } from '@ngrx/store';
import { OneClickLoadTermsAndConditionsAction } from '@ordersnproducts/modules/one-click-order/actions/one-click-terms-and-condition.actions';
import { OneClickFeatureConfig } from '@ordersnproducts/modules/one-click-order/models/one-click-feature-config';
import { OneClickElement } from '@ordersnproducts/modules/one-click-order/util/enums/one-click-element';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { RtlDetectorService } from '@ordersnproducts/modules/shared/services';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { FieldConfig } from '../../../shared/modules/configurable-form/models/field-configs/field-config.interface';
import { OneClickSelectOrderAction } from '../../actions/one-click-orders.actions';
import { OneClicTermsAndConditionsConfig } from '../../models/one-click-element-config';
import { getOneClickState, State } from '../../reducers';
import { getOneClickResult, getOneClickSelectedOrder } from '../../selectors/one-click-orders.selector';
import { OneClickConfigLoaderService } from '../../services/one-click-config-loader.service';
import { OneClickPreviewField } from '../../util/constants/preview-fields';
import { getOneClickOrderValidity } from './../../selectors/one-click-orders.selector';

@Component({
  selector: 'app-one-click-order-preview',
  templateUrl: './one-click-order-preview.component.html',
  styleUrls: ['./one-click-order-preview.component.scss', './custom-fields.scss'],
})
export class OneClickOrderPreviewComponent implements OnInit, OnDestroy {
  OneClickElement = OneClickElement;

  @ViewChild('preview', { read: ViewContainerRef, static: true }) entry: ViewContainerRef;
  @Output() ready = new EventEmitter<boolean>();
  public compontentRender: ComponentRef<{}>;
  public previewConfig$: Observable<FieldConfig[]>;
  public features: OneClickFeatureConfig;
  public hasBeenPlaced: boolean;
  public isValidOrder: boolean;
  public isLoading = false;
  public hasCountry: string;
  public enableCountry: boolean;
  public rtl = false;
  public termsAndConditions = {
    approved: false,
    loading: false,
  };
  public loadings$: Observable<{
    loading: boolean;
    placing: boolean;
  }>;
  public checkByDefault: boolean;
  private ngDestroy = new Subject();

  constructor(
    public rtlDetector: RtlDetectorService,
    private store: Store<State>,
    private configService: OneClickConfigLoaderService,
    private resolver: ComponentFactoryResolver,
  ) {}

  ngOnInit() {
    this.rtl = this.rtlDetector.activeLangRTL();
    this.store
      .select(getOneClickSelectedOrder)
      .pipe(
        filter((order) => !!order),
        distinctUntilChanged((prev, curr) => prev.orderId === curr.orderId),
        takeUntil(this.ngDestroy),
      )
      .subscribe((order: Order) => this.makePreviewForm(order));

    // Listen to submit
    this.store
      .select(getOneClickResult)
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((result) => (this.hasBeenPlaced = !!result));

    // Listen to features
    this.configService
      .getOneClickConfig()
      .pipe(
        takeUntil(this.ngDestroy),
        map((config) => config.features),
        tap((features) => (this.features = features)),
      )
      .subscribe((features) => {
        // Handle terms and conditions. Automatically check it if not defined
        const termsAndConditions =
          !!features && (features[OneClickElement.TERMS_AND_CONDITIONS] as OneClicTermsAndConditionsConfig);
        this.checkByDefault = termsAndConditions.checkByDefault;
        if (this.checkByDefault) {
          this.setTermsAndConditions(true);
        } else {
          this.setTermsAndConditions(!termsAndConditions);
        }
        // Handle order validity. Automatically check if not defined
        const requiresValidity = !!features && !!features[OneClickElement.ORDER_VALIDITY];
        this.checkOrderValidity(requiresValidity);
      });

    this.store.pipe(select(getOneClickState)).subscribe((state) => {
      const isValid = state.oneClickOrders.valid !== null;
      this.isLoading = isValid;
    });

    this.store.pipe(select(getCountry)).subscribe((country) => {
      this.hasCountry = country;
      const countryEnable = this.hasCountry === COUNTRY_CODE.SPAIN;
      this.enableCountry = countryEnable;
    });
  }

  public saveTermsAndConditionsDocument() {
    this.store.dispatch(new OneClickLoadTermsAndConditionsAction());
  }

  /**
   * Retrieves config needed and creates the component dynamically
   */
  makePreviewForm(order: Order) {
    this.configService
      .getOneClickPreviewConfig()
      .pipe(take(1))
      .subscribe((config) => {
        this.createComponent(ConfigurableFormComponent, config);
        this.store.dispatch(new OneClickSelectOrderAction(order));
      });
  }

  /**
   * Handles terms and conditions feature retrieved from config
   * If config does not has terms and conditions, set ready right away
   * @param features
   */
  public setTermsAndConditions(checked: boolean) {
    this.termsAndConditions.approved = checked;
    this.ready.emit(checked);
  }

  /**
   * Handles order validity when required.
   * If the product of the order is still available
   * @param requiresValidity
   */
  public checkOrderValidity(requiresValidity: boolean) {
    if (requiresValidity) {
      this.store
        .select(getOneClickOrderValidity)
        .pipe(
          filter((valid) => valid !== null),
          take(1),
        )
        .subscribe((valid) => (this.isValidOrder = valid));
    } else {
      this.isValidOrder = true;
    }
  }

  /**
   * Dynamically creates component
   */
  private createComponent(component, config: FieldConfig[], index = 0) {
    if (this.compontentRender) {
      this.compontentRender.destroy();
    }

    const factory = this.resolver.resolveComponentFactory(component);
    this.compontentRender = this.entry.createComponent(factory);
    this.compontentRender.instance['config'] = this.makeConfig(config, index);
    this.compontentRender.instance['mapItem'] = { index };
  }

  /**
   * Retrieves needed config of given parameter
   * @param full `true` gives entire config, `false` removes jobsite and pod input
   */
  private makeConfig(config: FieldConfig[], index: number) {
    if (index === 0) {
      return config.map((field) => {
        return { ...field, at: index };
      });
    } else {
      const _config = config.filter((field) => {
        return ![OneClickPreviewField.Jobsite, OneClickPreviewField.PointOfDelivery].includes(<any>field.name);
      });
      return _config.map((field) => {
        return { ...field, at: index };
      });
    }
  }

  /**
   * States whether all of the form input is valid.
   * If some control is invalid, return false
   */
  public isFormDataValid(): boolean {
    this.compontentRender.instance['updateValueAndValidity']();
    return Object.keys(this.compontentRender.instance['form']['controls']).every((key) => {
      const control: UntypedFormControl = this.compontentRender.instance['form']['controls'][key];
      control.markAsTouched();
      const invalid = control.invalid;
      return !invalid;
    });
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
